import { Temporal } from "temporal-polyfill";

import instance from "~/v2/api";
import { looseParse } from "~/v2/api/looseParse";
import { assetSchema } from "~/v2/api/rest/assets.schema";
import { getCryptoSnapshots, getStockSnapshots } from "~/v2/api/rest/data";
import { intradayBarsResponseSchema } from "./watchlistMarketData.schema";

/** The rendered sparkline is ~72px wide, so more points than this are wasted. */
export const MAX_SPARKLINE_POINTS = 60;

const SPARKLINE_TIMEFRAME = "5Min";
const MARKET_TIMEZONE = "America/New_York";

/** The project-wide heuristic: a crypto pair carries a slash, `BTC/USD`. */
export const isCryptoSymbol = (symbol: string): boolean => symbol.includes("/");

/** Market-day start; anchors stocks and crypto to the same sparkline window. */
export const getSessionStart = (now: number = Date.now()): string =>
  Temporal.Instant.fromEpochMilliseconds(now)
    .toZonedDateTimeISO(MARKET_TIMEZONE)
    .startOfDay()
    .toInstant()
    .toString();

export const watchlistMarketDataQueryKeys = {
  all: ["v3-poc", "watchlist-market-data"] as const,
  sparklines: (symbols: string[], sessionStart: string) =>
    [
      ...watchlistMarketDataQueryKeys.all,
      "sparklines",
      symbols.slice().sort().join(","),
      sessionStart,
    ] as const,
  assetMeta: (symbols: string[]) =>
    [
      ...watchlistMarketDataQueryKeys.all,
      "asset-meta",
      symbols.slice().sort().join(","),
    ] as const,
  livePrices: (symbols: string[]) =>
    [
      ...watchlistMarketDataQueryKeys.all,
      "live-prices",
      symbols.slice().sort().join(","),
    ] as const,
};

export interface WatchlistAssetMeta {
  name: string;
  isCrypto: boolean;
}

const buildBarsUrl = ({
  symbols,
  crypto,
  sessionStart,
}: {
  symbols: string[];
  crypto: boolean;
  sessionStart: string;
}): string => {
  const params = new URLSearchParams({
    symbols: symbols.join(","),
    start: sessionStart,
    timeframe: SPARKLINE_TIMEFRAME,
    limit: "10000",
  });
  if (!crypto) {
    params.set("adjustment", "split");
  }
  const base = crypto
    ? `${window.env.CRYPTO_DATA_API_URL}/bars`
    : `${window.env.DATA_API_URL}/stocks/bars`;
  return `${base}?${params.toString()}`;
};

/** Evenly spaced subset that keeps the endpoints, so trend colour is unchanged. */
export const downsample = (
  values: number[],
  limit = MAX_SPARKLINE_POINTS,
): number[] => {
  if (values.length <= limit) {
    return values;
  }
  const step = (values.length - 1) / (limit - 1);
  return Array.from(
    { length: limit },
    (_, index) => values[Math.round(index * step)],
  );
};

/**
 * One asset class's bars, following `next_page_token` to exhaustion. The
 * endpoint `limit` counts points across all symbols, not per symbol, so
 * pagination is required for completeness — not just for long windows.
 */
const fetchBarsPage = async (
  url: string,
): Promise<Record<string, number[]>> => {
  const closesBySymbol: Record<string, number[]> = {};
  const seenTokens = new Set<string>();
  let pageToken: string | undefined;

  do {
    const pageUrl = pageToken
      ? `${url}&page_token=${encodeURIComponent(pageToken)}`
      : url;
    const { data } = await instance.get<unknown>(pageUrl);
    const parsed = looseParse(
      intradayBarsResponseSchema,
      data,
      "v3.getWatchlistSparklines",
    );

    // looseParse returns the raw payload on failure, so guard the shape.
    const bars = parsed?.bars;
    if (bars && typeof bars === "object") {
      for (const [symbol, symbolBars] of Object.entries(bars)) {
        if (!Array.isArray(symbolBars)) {
          continue;
        }
        const closes = symbolBars
          .filter((bar) => typeof bar?.c === "number")
          .map((bar) => bar.c);
        closesBySymbol[symbol] = [...(closesBySymbol[symbol] ?? []), ...closes];
      }
    }

    const nextToken =
      typeof parsed?.next_page_token === "string" && parsed.next_page_token
        ? parsed.next_page_token
        : undefined;
    pageToken = nextToken && !seenTokens.has(nextToken) ? nextToken : undefined;
    if (pageToken) {
      seenTokens.add(pageToken);
    }
  } while (pageToken);

  return closesBySymbol;
};

/**
 * Today's intraday closes per symbol, oldest → newest. Split by asset class
 * (separate hosts) into at most two requests; `allSettled` so one class
 * failing does not blank the other.
 */
export const getWatchlistSparklines = async ({
  symbols,
  sessionStart,
}: {
  symbols: string[];
  sessionStart: string;
}): Promise<Record<string, number[]>> => {
  const cryptoSymbols = symbols.filter(isCryptoSymbol);
  const stockSymbols = symbols.filter((symbol) => !isCryptoSymbol(symbol));

  const requests = [
    stockSymbols.length
      ? buildBarsUrl({ symbols: stockSymbols, crypto: false, sessionStart })
      : undefined,
    cryptoSymbols.length
      ? buildBarsUrl({ symbols: cryptoSymbols, crypto: true, sessionStart })
      : undefined,
  ].filter((url): url is string => url !== undefined);

  const results = await Promise.allSettled(requests.map(fetchBarsPage));

  const sparklines: Record<string, number[]> = {};
  for (const result of results) {
    if (result.status !== "fulfilled") {
      continue;
    }
    for (const [symbol, closes] of Object.entries(result.value)) {
      sparklines[symbol] = downsample(closes);
    }
  }
  return sparklines;
};

export interface WatchlistLivePrice {
  price?: number;
  previousClose?: number;
  dayHigh?: number;
  dayLow?: number;
}

/** Trade print only — deliberately ignores latestQuote / midpoint. */
export const mapSnapshotToLivePrice = (snap: {
  latestTrade?: { p?: number };
  prevDailyBar?: { c?: number };
  dailyBar?: { h?: number; l?: number };
}): WatchlistLivePrice => {
  const out: WatchlistLivePrice = {};
  if (typeof snap.latestTrade?.p === "number") {
    out.price = snap.latestTrade.p;
  }
  if (typeof snap.prevDailyBar?.c === "number") {
    out.previousClose = snap.prevDailyBar.c;
  }
  if (typeof snap.dailyBar?.h === "number") {
    out.dayHigh = snap.dailyBar.h;
  }
  if (typeof snap.dailyBar?.l === "number") {
    out.dayLow = snap.dailyBar.l;
  }
  return out;
};

/**
 * Latest trade + previous close per symbol via stock/crypto snapshots.
 * One class failing does not blank the other (`allSettled`).
 */
export const getWatchlistLivePrices = async (
  symbols: string[],
): Promise<Record<string, WatchlistLivePrice>> => {
  const cryptoSymbols = symbols.filter(isCryptoSymbol);
  const stockSymbols = symbols.filter((symbol) => !isCryptoSymbol(symbol));

  const emptyStock: Awaited<ReturnType<typeof getStockSnapshots>> = {};
  const emptyCrypto: Awaited<ReturnType<typeof getCryptoSnapshots>> = {
    snapshots: {},
  };

  const [stockResult, cryptoResult] = await Promise.allSettled([
    stockSymbols.length > 0 ? getStockSnapshots(stockSymbols) : emptyStock,
    cryptoSymbols.length > 0
      ? getCryptoSnapshots({ symbols: cryptoSymbols })
      : emptyCrypto,
  ]);

  const prices: Record<string, WatchlistLivePrice> = {};

  if (stockResult.status === "fulfilled") {
    for (const [symbol, snap] of Object.entries(stockResult.value)) {
      prices[symbol] = mapSnapshotToLivePrice(snap);
    }
  }

  if (cryptoResult.status === "fulfilled") {
    for (const [symbol, snap] of Object.entries(cryptoResult.value.snapshots)) {
      prices[symbol] = mapSnapshotToLivePrice(snap);
    }
  }

  return prices;
};

/**
 * One asset by symbol. Not v2's `getAsset`: it interpolates the symbol raw, so
 * a coin pair's slash becomes an extra path segment and 404s — encode it.
 */
const getAssetBySymbol = async (symbol: string) => {
  const { data } = await instance.get<unknown>(
    `/assets/${encodeURIComponent(symbol)}`,
  );
  return looseParse(assetSchema, data, "v3.getWatchlistAssetMeta");
};

/**
 * Names and asset class per symbol, fanned out one request each: `getAssets()`
 * answers in one call but returns the whole tradable universe. A failed lookup
 * is dropped, so its row renders without a name.
 */
export const getWatchlistAssetMeta = async (
  symbols: string[],
): Promise<Record<string, WatchlistAssetMeta>> => {
  const results = await Promise.allSettled(
    symbols.map(async (symbol) => ({
      symbol,
      asset: await getAssetBySymbol(symbol),
    })),
  );

  const meta: Record<string, WatchlistAssetMeta> = {};
  for (const result of results) {
    if (result.status !== "fulfilled") {
      continue;
    }
    const { symbol, asset } = result.value;
    if (typeof asset?.name !== "string") {
      continue;
    }
    meta[symbol] = {
      name: asset.name,
      isCrypto: asset.class === "crypto" || isCryptoSymbol(symbol),
    };
  }
  return meta;
};
